import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import addVariants from '../utils/addVariants/index.ts';
import useRect from '../utils/useRect/index.ts';
import useWindowSize from '../utils/useWindowSize/index.ts';

export const VARIANTS = [
  'default',
  'dense',
  'thin',
  'black',
  'dark',
  'light',
  'no-border',
  'no-padding'
];
const mapEls = els =>
  React.Children.map(els, el => {
    if (el && el.type === React.Fragment) {
      return <>{mapEls(el.props.children)}</>;
    }

    return <span className={addVariants('aglmd-app-bar__slot__item', styles)}>{el}</span>;
  });
/**
 * An App Bar is a tool bar that has slots to put items into: left, right, center and extension
 * The extension is meant for anything to be put below the main tools of the app bar.
 */
const AppBar = React.forwardRef(
  (
    {
      variant,
      className,
      extension: extensionSlot,
      left: leftSlot,
      right: rightSlot,
      center: centerSlot,
      isFixed,
      children,
      isLoading,
      ...props
    },
    passedRef
  ) => {
    const [measuringRef, { height, top, width }] = useRect();
    const { height: windowHeight } = useWindowSize();

    let rightEl = null;
    let leftEl = null;
    let centerEl = <div className={addVariants('aglmd-app-bar__center', styles, 'spacer-only')} />;
    let extensionEl = null;
    let loadingIndicator;

    if (rightSlot) {
      rightEl = (
        <div className={addVariants('aglmd-app-bar__right', styles)}>
          <div className={addVariants('aglmd-app-bar__slot', styles)}>{mapEls(rightSlot)}</div>
        </div>
      );
    }

    if (leftSlot) {
      leftEl = (
        <div className={addVariants('aglmd-app-bar__left', styles)}>
          <div className={addVariants('aglmd-app-bar__slot', styles)}>{mapEls(leftSlot)}</div>
        </div>
      );
    }

    if (centerSlot) {
      centerEl = (
        <div className={addVariants('aglmd-app-bar__center', styles)}>
          <div className={addVariants('aglmd-app-bar__slot', styles)}>{mapEls(centerSlot)}</div>
        </div>
      );
    }

    if (extensionSlot) {
      extensionEl = (
        <div className={addVariants('aglmd-app-bar__extension__container', styles)}>
          {extensionSlot}
        </div>
      );
    }

    if (isLoading) {
      loadingIndicator = (
        <div className={styles.loading__indicator__track}>
          <div className={styles.loading__indicator} />
        </div>
      );
    }

    return (
      <div
        className={styles['aglmd-app-bar__wrapper']}
        style={{
          paddingTop: isFixed && `${height}px`
        }}
      >
        <div
          className={classnames(
            addVariants(
              'aglmd-app-bar',
              styles,
              variant,
              isFixed && 'fixed',
              extensionSlot && 'has-extension'
            ),
            className
          )}
          ref={measuringRef}
          {...props}
        >
          <div className={addVariants('aglmd-app-bar__container', styles)}>
            {leftEl}
            {centerEl}
            {rightEl}
          </div>
          {extensionEl}
          {loadingIndicator}
        </div>
        {children && (
          <div
            className={addVariants('aglmd-app-bar__page-container', styles, isLoading && 'loading')}
            style={{
              height: isFixed && `${windowHeight - (height + top)}px`,
              width: isFixed && `${width}px`
            }}
            ref={passedRef}
          >
            {children}
          </div>
        )}
      </div>
    );
  }
);

AppBar.propTypes = {
  /**
   * The rest of the page to display
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]),
  /**
   * The leftmost content
   */
  left: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([false])]),
  /**
   * The rightmost content
   */
  right: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([false])]),
  /**
   * The content in the center
   */
  center: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([false])]),
  /**
   * Whether the app bar is fixed to the top of the screen
   */
  isFixed: PropTypes.bool,
  /**
   * The content just below
   */
  extension: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([false])]),
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Whether the app bar's content is loading
   */
  isLoading: PropTypes.bool
};

AppBar.defaultProps = {
  className: '',
  variant: 'light',
  children: null,
  left: false,
  right: false,
  center: false,
  extension: false,
  isFixed: false,
  isLoading: false
};

export default AppBar;
